import { useEffect } from 'react';
import { SOFTWARE_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { useService } from '@atlassian/jira-common-services/src/use-service/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { SHOULD_SHOW_WELCOME_ONBOARDING_MODAL } from '@atlassian/jira-onboarding-core/src/constants.tsx';
import { sessionStorageProvider } from '@atlassian/jira-onboarding-core/src/utils/index.tsx';
import { memoizedFetchSitePersonalization } from '@atlassian/jira-personalization-service/src/services/site/index.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { FREE_EDITION } from '@atlassian/jira-shared-types/src/edition.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';

const JSW_PROJECT_RENAMED_TRAIT =
	'jira-software_track_project_updated_onboardingprojectdetailsscreen_first';

export const getIsProjectUpdatedFromWelcomeModal = async (cloudId: string) => {
	const sessionStorageValue = sessionStorageProvider.get('project_updated_in_welcome_modal');
	if (sessionStorageValue === 'true' || sessionStorageValue === 'false') {
		return sessionStorageValue === 'true';
	}
	const response = await memoizedFetchSitePersonalization({ cloudId });
	const targetTraitValue = response.find(
		(traits) => traits.name === JSW_PROJECT_RENAMED_TRAIT,
	)?.value;
	if (targetTraitValue) {
		sessionStorageProvider.set('project_updated_in_welcome_modal', true);
		return true;
	}
	return false;
};

export const useIsEditableToInline = () => {
	const { data } = useProjectContext();
	const { software: softwareEdition } = useAppEditions();
	const projectUpdatedLocalValue = sessionStorageProvider.get('project_updated_in_welcome_modal');
	const shouldShowWelcomeModal = sessionStorageProvider.get(SHOULD_SHOW_WELCOME_ONBOARDING_MODAL);

	const isBasicTargetted =
		!shouldShowWelcomeModal && // Side bar loaded earlier than welcome modal, and we don't want to fire our exposure before welcome modal
		data?.projectType === SOFTWARE_PROJECT &&
		softwareEdition === FREE_EDITION &&
		data?.isProjectAdmin &&
		!(typeof projectUpdatedLocalValue === 'boolean' && projectUpdatedLocalValue);

	const cloudId = useCloudId();

	const {
		loading,
		error,
		data: isProjectUpdated,
		fetch,
	} = useService<boolean>(() => getIsProjectUpdatedFromWelcomeModal(cloudId));

	useEffect(() => {
		if (!isBasicTargetted) {
			return;
		}
		if (loading || error || isProjectUpdated !== undefined) {
			return;
		}
		fetch();
	}, [isProjectUpdated, fetch, isBasicTargetted, loading, error]);

	useEffect(() => {
		if (error) {
			fireErrorAnalytics({
				error,
				meta: {
					id: 'getIsProjectUpdatedFromWelcomeModal',
					packageName: 'jiraNavigationAppsSidebarInlineConfigProjectHeader',
				},
			});
		}
	}, [error]);

	return () => isBasicTargetted && isProjectUpdated === false;
};
